import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import { useToast } from '../components/ui/Toast';
import type { PlatformEvent } from '../types/events';
import type {
  InstallPluginInput,
  PluginInstallResult,
  PluginUpstream,
  ExploreRequest,
  ExploreResponse,
} from '../types/plugins';
import type { Plugin, PaginatedResponse, PluginStatus } from '../types';

const PLUGIN_SOURCE = 'optr.plugin';

function useSpacePluginApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).plugins, [spaceId]);
}

export function useSpacePlugins() {
  const spaceId = useSpaceId();
  const api = useSpacePluginApi();
  return useQuery({
    queryKey: ['space', spaceId, 'plugins'],
    queryFn: () => api.list(),
  });
}

export function useSpacePlugin(
  pluginId: string | undefined,
  opts: { expand?: ('skills' | 'agents')[] } = {},
) {
  const spaceId = useSpaceId();
  const api = useSpacePluginApi();
  const expandKey = opts.expand?.slice().sort().join(',') ?? '';
  return useQuery({
    queryKey: ['space', spaceId, 'plugins', pluginId, { expand: expandKey }],
    queryFn: () => api.get(pluginId!, opts),
    enabled: !!pluginId,
  });
}

/**
 * Pre-install repo inspection. Each call is user-initiated (Inspect button
 * click or ref-dropdown change), so a mutation with no caching is the right
 * shape — react-query won't serve stale data across modal opens.
 */
export function useExplorePlugin() {
  const api = useSpacePluginApi();
  return useMutation<ExploreResponse, Error, ExploreRequest>({
    mutationFn: (input) => api.explore(input),
  });
}

export function useInstallPlugin() {
  const spaceId = useSpaceId();
  const api = useSpacePluginApi();
  const qc = useQueryClient();

  return useMutation<PluginInstallResult, Error, InstallPluginInput>({
    mutationFn: (input) => api.install(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'plugins'] });
      // Import mode creates skills + agents synchronously.
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'skills'] });
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'agents'] });
    },
  });
}

export function useSwitchPluginRef() {
  const spaceId = useSpaceId();
  const api = useSpacePluginApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ pluginId, ref }: { pluginId: string; ref: string }) =>
      api.switchRef(pluginId, ref),
    onSuccess: (_data, { pluginId }) => {
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'plugins'] });
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'plugins', pluginId] });
    },
  });
}

export function useCheckPluginUpdates() {
  const spaceId = useSpaceId();
  const api = useSpacePluginApi();
  const qc = useQueryClient();

  return useMutation<PluginUpstream, Error, string>({
    mutationFn: (pluginId: string) => api.checkUpdates(pluginId),
    onSuccess: (upstream, pluginId) => {
      // Write the upstream directly into the cached plugin instead of
      // relying on a refetch — the backend's GET /plugins/{id} doesn't
      // always echo the upstream block, which would leave the detail
      // page spinning forever waiting for `plugin.upstream` to populate.
      qc.setQueriesData<Plugin | undefined>(
        { queryKey: ['space', spaceId, 'plugins', pluginId] },
        (old) => (old ? { ...old, upstream } : old),
      );
      qc.setQueriesData<PaginatedResponse<Plugin> | undefined>(
        { queryKey: ['space', spaceId, 'plugins'], exact: true },
        (old) =>
          old
            ? {
                ...old,
                items: old.items.map((p) =>
                  p.pluginId === pluginId ? { ...p, upstream } : p,
                ),
              }
            : old,
      );
    },
  });
}

export function useSyncPlugin() {
  const spaceId = useSpaceId();
  const api = useSpacePluginApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (pluginId: string) => api.sync(pluginId),
    onSuccess: (_data, pluginId) => {
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'plugins'] });
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'plugins', pluginId] });
    },
  });
}

/**
 * Toggle the auto-update flag. The backend's call is passive — it doesn't
 * enqueue a sync, just changes what the next scheduled scan does. We write
 * the returned Plugin directly into cache (same pattern as
 * `useCheckPluginUpdates`) so the Switch flips immediately rather than
 * waiting on a refetch.
 */
export function useSetAutoUpdate() {
  const spaceId = useSpaceId();
  const api = useSpacePluginApi();
  const qc = useQueryClient();

  return useMutation<Plugin, Error, { pluginId: string; autoUpdate: boolean }>({
    mutationFn: ({ pluginId, autoUpdate }) =>
      api.setAutoUpdate(pluginId, autoUpdate),
    onSuccess: (updated, { pluginId }) => {
      qc.setQueriesData<Plugin | undefined>(
        { queryKey: ['space', spaceId, 'plugins', pluginId] },
        (old) => (old ? { ...old, autoUpdate: updated.autoUpdate } : old),
      );
      qc.setQueriesData<PaginatedResponse<Plugin> | undefined>(
        { queryKey: ['space', spaceId, 'plugins'], exact: true },
        (old) =>
          old
            ? {
                ...old,
                items: old.items.map((p) =>
                  p.pluginId === pluginId
                    ? { ...p, autoUpdate: updated.autoUpdate }
                    : p,
                ),
              }
            : old,
      );
    },
  });
}

export function useUninstallPlugin() {
  const spaceId = useSpaceId();
  const api = useSpacePluginApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (pluginId: string) => api.uninstall(pluginId),
    onSuccess: (_data, pluginId) => {
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'plugins'] });
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'plugins', pluginId] });
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'skills'] });
      qc.invalidateQueries({ queryKey: ['space', spaceId, 'agents'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Event subscriptions
// ---------------------------------------------------------------------------

function eventPluginId(event: PlatformEvent): string | undefined {
  const data = event.data as Record<string, unknown> | undefined;
  return (
    (event as unknown as { pluginId?: string }).pluginId ??
    event.subjectId ??
    (data?.pluginId as string | undefined)
  );
}

interface ResourceCounts {
  skills?: number;
  agents?: number;
  created?: number;
  updated?: number;
  unchanged?: number;
  removed?: number;
  mcpServersReported?: number;
}

function formatCounts(counts: ResourceCounts | undefined): string {
  if (!counts) return '';
  const parts: string[] = [];
  if (counts.created !== undefined) parts.push(`${counts.created} created`);
  if (counts.updated !== undefined) parts.push(`${counts.updated} updated`);
  if (counts.unchanged !== undefined && counts.unchanged > 0)
    parts.push(`${counts.unchanged} unchanged`);
  if (counts.removed !== undefined && counts.removed > 0)
    parts.push(`${counts.removed} removed`);
  if (counts.skills !== undefined) parts.push(`${counts.skills} skills`);
  if (counts.agents !== undefined) parts.push(`${counts.agents} agents`);
  return parts.join(' · ');
}

/**
 * Bus subscription used by the plugin list page. Invalidates the plugin list
 * (and skills/agents when relevant) on every `optr.plugin.*` event, and emits
 * a toast on completion/failure events.
 */
export function usePluginEventBus() {
  const spaceId = useSpaceId();
  const qc = useQueryClient();
  const { subscribe } = usePlatformEvents();
  const { success, error: showError } = useToast();

  useEffect(() => {
    const unsubscribe = subscribe(
      [{ source: PLUGIN_SOURCE, spaceId }],
      (event) => {
        qc.invalidateQueries({ queryKey: ['space', spaceId, 'plugins'] });

        const pluginId = eventPluginId(event);
        const data = event.data as Record<string, unknown>;
        // Optimistic cache write — the backend's GET /plugins can lag behind
        // the event by a beat (DDB read-after-write), so the immediate refetch
        // sometimes still returns the previous status. Writing the terminal
        // status straight into the cache flips the UI now; the refetch then
        // confirms. Mirrors the pattern in `useCheckPluginUpdates` above.
        const writeStatus = (status: PluginStatus, failureReason?: string) => {
          if (!pluginId) return;
          qc.setQueriesData<PaginatedResponse<Plugin> | undefined>(
            { queryKey: ['space', spaceId, 'plugins'], exact: true },
            (old) =>
              old
                ? {
                    ...old,
                    items: old.items.map((p) =>
                      p.pluginId === pluginId
                        ? { ...p, status, ...(failureReason ? { failureReason } : {}) }
                        : p,
                    ),
                  }
                : old,
          );
          qc.setQueriesData<Plugin | undefined>(
            { queryKey: ['space', spaceId, 'plugins', pluginId] },
            (old) =>
              old
                ? { ...old, status, ...(failureReason ? { failureReason } : {}) }
                : old,
          );
        };

        switch (event.eventType) {
          case 'plugin.install.completed': {
            const counts = data?.resourceCounts as ResourceCounts | undefined;
            writeStatus('active');
            qc.invalidateQueries({ queryKey: ['space', spaceId, 'skills'] });
            qc.invalidateQueries({ queryKey: ['space', spaceId, 'agents'] });
            success(`Plugin installed — ${formatCounts(counts) || 'ready'}`);
            break;
          }
          case 'plugin.sync.completed': {
            const counts = data?.resourceCounts as ResourceCounts | undefined;
            writeStatus('active');
            qc.invalidateQueries({ queryKey: ['space', spaceId, 'skills'] });
            qc.invalidateQueries({ queryKey: ['space', spaceId, 'agents'] });
            success(`Plugin synced — ${formatCounts(counts) || 'up to date'}`);
            break;
          }
          case 'plugin.uninstalled': {
            qc.invalidateQueries({ queryKey: ['space', spaceId, 'skills'] });
            qc.invalidateQueries({ queryKey: ['space', spaceId, 'agents'] });
            break;
          }
          case 'plugin.install.failed':
          case 'plugin.sync.failed': {
            const err = (data?.error as { message?: string } | undefined)?.message;
            writeStatus('failed', err);
            showError(err || 'Plugin operation failed');
            break;
          }
        }
      },
    );
    return unsubscribe;
  }, [spaceId, subscribe, qc, success, showError]);
}

/**
 * Detail-page subscription — filters by pluginId in the handler. Invalidates
 * the per-plugin query on every event and surfaces failure toasts. Completion
 * toasts are already emitted by `usePluginEventBus` on the list page; we
 * don't double-fire when both pages are mounted.
 */
export function usePluginDetailEvents(pluginId: string | undefined) {
  const spaceId = useSpaceId();
  const qc = useQueryClient();
  const { subscribe } = usePlatformEvents();
  const { error: showError } = useToast();

  useEffect(() => {
    if (!pluginId) return;
    const unsubscribe = subscribe(
      [{ source: PLUGIN_SOURCE, spaceId }],
      (event) => {
        if (eventPluginId(event) !== pluginId) return;
        qc.invalidateQueries({ queryKey: ['space', spaceId, 'plugins', pluginId] });

        if (
          event.eventType === 'plugin.install.failed' ||
          event.eventType === 'plugin.sync.failed'
        ) {
          const data = event.data as Record<string, unknown>;
          const err = (data?.error as { message?: string } | undefined)?.message;
          showError(err || 'Plugin operation failed');
        }
      },
    );
    return unsubscribe;
  }, [pluginId, spaceId, subscribe, qc, showError]);
}

// Lean on WS events as the primary signal. Wait 30s before falling back to
// polling, then poll at 15s intervals — install/sync events normally arrive
// within seconds, so this only ever fires when the WS bus is degraded.
const COMPLETION_FALLBACK_DELAY_MS = 30_000;
const COMPLETION_POLL_INTERVAL_MS = 15_000;
const COMPLETION_STALL_AFTER_MS = 5 * 60 * 1000;

/**
 * Watches a syncing plugin to terminal state. Used by PluginDetail to swap
 * the syncing banner. Resolves via WS event when available; falls back to a
 * 3s poll if no event arrives within 10s; surfaces `stalled` after 5 min.
 */
export function useAwaitPluginCompletion(pluginId: string | undefined) {
  const spaceId = useSpaceId();
  const qc = useQueryClient();
  const { subscribe } = usePlatformEvents();
  const [state, setState] = useState({ done: false, failed: false, stalled: false });
  const startedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!pluginId) return;
    startedAtRef.current = Date.now();
    setState({ done: false, failed: false, stalled: false });

    let cleared = false;
    let pollInterval: ReturnType<typeof setInterval> | undefined;

    const wsUnsub = subscribe([{ source: PLUGIN_SOURCE, spaceId }], (event) => {
      if (cleared) return;
      if (eventPluginId(event) !== pluginId) return;
      if (
        event.eventType === 'plugin.install.completed' ||
        event.eventType === 'plugin.sync.completed'
      ) {
        setState({ done: true, failed: false, stalled: false });
      } else if (
        event.eventType === 'plugin.install.failed' ||
        event.eventType === 'plugin.sync.failed'
      ) {
        setState({ done: true, failed: true, stalled: false });
      }
    });

    const fallback = setTimeout(() => {
      if (cleared) return;
      pollInterval = setInterval(() => {
        if (cleared) return;
        if (Date.now() - startedAtRef.current >= COMPLETION_STALL_AFTER_MS) {
          setState((s) => (s.done ? s : { ...s, stalled: true }));
          if (pollInterval) clearInterval(pollInterval);
          return;
        }
        qc.invalidateQueries({ queryKey: ['space', spaceId, 'plugins', pluginId] });
      }, COMPLETION_POLL_INTERVAL_MS);
    }, COMPLETION_FALLBACK_DELAY_MS);

    return () => {
      cleared = true;
      wsUnsub();
      clearTimeout(fallback);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [pluginId, spaceId, subscribe, qc]);

  return state;
}
